import React, { ReactNode } from 'react'
import { graphql, ChildMutateProps, withApollo, WithApolloClient, FetchResult } from 'react-apollo'
import { Observable } from 'apollo-client/util/Observable'
import ShoppingCartQueryDocument from './ShoppingCart.gql'
import ShoppingCartSubscriptionDocument from './ShoppingCartSubscription.gql'
import SyncShoppingCartDocument from './SyncShoppingCart.gql'
import {
  ShoppingCartComponent,
  ShoppingCartQuery,
  ShoppingCartQueryVariables,
  ShoppingCartSubscriptionSubscription as ShoppingCartSubscriptionType,
  SyncShoppingCartMutation as SyncShoppingCartMutationType,
  ShoppingCartOrderFragment
} from '#src/generated/graphql'
import { WithIdentityProps, withIdentity } from '#src/containers/Identity'

type CartContext = {
  order?: ShoppingCartOrderFragment
}

// Context
const Cart = React.createContext<CartContext>({ order: undefined })

// Consumer
const CartConsumer = Cart.Consumer

// Provider
const CartProvider: React.SFC<{}> = ({ children }) => (
  <CartObserver>
    {({ order }) => {
      return (
        <Cart.Provider
          value={{
            order
          }}
        >
          {children}
        </Cart.Provider>
      )
    }}
  </CartObserver>
)

interface ExternalCartObserverProps {
  children: (observation: { order?: ShoppingCartOrderFragment }) => ReactNode
}

interface CartObserverBaseProps
  extends ChildMutateProps<
      WithApolloClient<ExternalCartObserverProps & WithIdentityProps>,
      SyncShoppingCartMutationType
    > {}

interface CartObserverBaseState {
  orderId?: string
}

class CartObserverBase extends React.Component<CartObserverBaseProps, CartObserverBaseState> {
  public state: CartObserverBaseState = {}
  public observer?: Observable<FetchResult<ShoppingCartSubscriptionType>>
  public subscription?: ZenObservable.Subscription

  // If user signs in, sync cart
  public componentDidUpdate(prevProps: CartObserverBaseProps) {
    if (!prevProps.myself && this.props.myself && this.props.mutate) {
      this.initialCartManagement()
    }

    if (prevProps.myself && !this.props.myself) {
      this.unsubscribe()
    }
  }

  public componentDidMount() {
    if (this.props.mutate && this.props.myself) {
      this.initialCartManagement()
    }
  }

  public initialCartManagement() {
    /**
     * State management strategy:
     *
     *  1. Use the `syncCart` mutation to get current order ID
     *  2. Use ShoppingCartQuery to get order data
     *  2. Use a GraphQL subscription to subscribe to all Cart changes
     *  3. Write those changes directly to the Apollo Cache so that all <Query>'s are notified
     */

    // Sync the cart with the server and save the cart's orderId to state
    this.props.mutate().then(response => {
      if (response && response.data && response.data.syncOrder.order) {
        this.setState({
          orderId: response.data.syncOrder.order.id
        })
      }
    })

    // Create a GraphQL subscription for future Cart/Order updates
    this.observer = this.props.client.subscribe({
      query: ShoppingCartSubscriptionDocument
    })

    // On any subscription event, entirely replace the Order in the Apollo Cache with the new result
    this.subscription = this.observer.subscribe(({ data }) => {
      if (data && data.userCartLineItemsChanged) {
        this.props.client.writeQuery<ShoppingCartQuery, ShoppingCartQueryVariables>({
          query: ShoppingCartQueryDocument,
          variables: {
            id: data.userCartLineItemsChanged.id
          },
          data: {
            __typename: 'Query',
            order: data.userCartLineItemsChanged
          }
        })

        /**
         * If the orderId has changed (e.g. user has purchased an order) we must update
         * the provider to start tracking the new cart order
         */
        if (data.userCartLineItemsChanged.id !== this.state.orderId) {
          this.setState({ orderId: data.userCartLineItemsChanged.id })
        }
      }
    })
  }

  public componentWillUnmount() {
    this.unsubscribe()
  }

  public unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      delete this.subscription
    }
  }

  public render() {
    if (!this.props.myself) {
      return this.props.children({ order: undefined })
    }
    return (
      // Skip this query until we have the orderId in state
      // The purpose of this query is to read cache updates. By the time it runs the cache will be updated
      // DW: If we allow the query to make network requests it will fail when someone logs out and the cache is reset.
      // When the cache is reset on logout this query tries to fire even though they don't have permission
      <ShoppingCartComponent
        skip={!this.state.orderId}
        fetchPolicy="cache-only"
        variables={{ id: this.state.orderId! }}
      >
        {({ data }) => {
          // Pass through undefined if data object does not exist so that child can handle loading state
          return this.props.children({ order: data && data.order })
        }}
      </ShoppingCartComponent>
    )
  }
}

const CartObserver = withIdentity(
  graphql<ExternalCartObserverProps, SyncShoppingCartMutationType, {}, CartObserverBaseProps>(
    SyncShoppingCartDocument
  )(withApollo(CartObserverBase))
)

export { CartProvider, CartConsumer, CartObserver }
